import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown, ArrowUp, Eye, EyeOff, LayoutList, Loader2, RotateCcw, Save,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { SITE_THEMES } from '../lib/siteThemes';
import {
  HOME_BLOCK_HINTS, HOME_BLOCK_LABELS, HOME_BLOCKS, isDefaultLayout, resolveHomeLayout,
} from '../lib/homeLayout';
import { useAdminPermissions } from './lib/permissions';
import { AdminSkeleton } from './components/PageState';

/** Ana sayfa bölümlerinin sırası ve görünürlüğü.
 *
 * Katalog `lib/homeLayout.js`'tedir ve sitenin kendisi de onu okur; bu ekran
 * yalnızca yöneticinin kararını yazar (`PUT /admin/home-layout/{şema}`). Her
 * şemanın bölümleri farklı olduğu için düzen şema başınadır ve açılışta
 * yayındaki şema seçili gelir.
 */

// Yalnızca kataloğu olan şemalar düzenlenebilir: başka bir şema ana sayfasını
// klasik düzenle çizer ve onun düzeni `default` kaydıdır.
const EDITABLE_THEMES = SITE_THEMES.filter((theme) => HOME_BLOCKS[theme.id]);

const themeOf = (value) => (HOME_BLOCKS[value] ? value : 'default');

const HomeLayout = () => {
  const { can } = useAdminPermissions();
  const { toast } = useToast();
  const canWrite = can('content.write');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTheme, setActiveTheme] = useState('default');
  const [theme, setTheme] = useState('default');
  const [saved, setSaved] = useState({});
  const [blocks, setBlocks] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/home-layout');
      const active = themeOf(data.active_theme);
      setActiveTheme(active);
      setTheme(active);
      setSaved(data.themes || {});
    } catch (error) {
      toast({
        title: 'Ana sayfa düzeni yüklenemedi',
        description: error.response?.data?.detail || 'Kayıt okunamadı',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setBlocks(resolveHomeLayout(theme, saved[theme]));
  }, [theme, saved]);

  const initial = useMemo(() => resolveHomeLayout(theme, saved[theme]), [theme, saved]);
  const dirty = JSON.stringify(blocks) !== JSON.stringify(initial);
  const atDefault = isDefaultLayout(theme, blocks);

  const move = (index, delta) => {
    setBlocks((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggle = (index) => {
    setBlocks((current) => current.map((item, position) => (
      position === index ? { ...item, visible: !item.visible } : item
    )));
  };

  const persist = async (payload) => {
    setSaving(true);
    try {
      const { data } = await client.put(`/admin/home-layout/${theme}`, { blocks: payload });
      setSaved((current) => {
        const next = { ...current };
        if (data.blocks?.length) next[theme] = data.blocks;
        else delete next[theme];
        return next;
      });
      toast({ title: 'Ana sayfa düzeni kaydedildi' });
    } catch (error) {
      toast({
        title: 'Ana sayfa düzeni kaydedilemedi',
        description: error.response?.data?.detail || 'Değişiklikler uygulanamadı',
      });
    } finally {
      setSaving(false);
    }
  };

  // Varsayılandaki düzen kayda boş liste olarak yazılır: sonradan eklenen
  // bir bölüm de o zaman kendi yerinde görünür.
  const save = () => persist(atDefault ? [] : blocks);
  const reset = () => persist([]);

  if (loading) return <AdminSkeleton rows={8} label="Ana sayfa düzeni yükleniyor" />;

  const themeName = (id) => SITE_THEMES.find((item) => item.id === id)?.name || id;

  return (
    <div className="admin-home-layout-page">
      <div className="admin-page-heading">
        <div className="admin-page-heading-icon"><LayoutList size={22} /></div>
        <div>
          <h1>Ana Sayfa Düzeni</h1>
          <p>Ana sayfadaki bölümlerin sırasını değiştirin, istemediklerinizi gizleyin</p>
        </div>
        <div className="admin-page-heading-meta">
          Yayındaki şema: {themeName(activeTheme)}
        </div>
      </div>

      {!canWrite && (
        <div className="admin-card p-4 text-sm text-amber-200">
          Bu düzeni görüntüleyebilirsiniz; değiştirmek için içerik yazma yetkisi gerekir.
        </div>
      )}

      <section className="admin-card admin-merit-selected" aria-labelledby="home-layout-list">
        <div className="admin-merit-panel-head">
          <div>
            <h2 id="home-layout-list">Bölümler</h2>
            <p>
              Buradaki sıra sitede yukarıdan aşağıya bölüm sırasıdır. İçi boş bir
              bölüm (ör. oyun seçilmemiş bir şerit) açık olsa da sitede çizilmez.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="home-layout-theme">Şema</label>
            <select
              id="home-layout-theme"
              className="admin-input admin-input-w-md"
              value={theme}
              onChange={(event) => setTheme(event.target.value)}
              disabled={saving}
            >
              {EDITABLE_THEMES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}{item.id === activeTheme ? ' (yayında)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="admin-btn-secondary inline-flex items-center gap-2"
              onClick={reset}
              disabled={!canWrite || saving || (!saved[theme] && atDefault)}
            >
              <RotateCcw size={15} />
              Varsayılana Dön
            </button>
            <button
              type="button"
              className="admin-btn-primary inline-flex items-center gap-2"
              onClick={save}
              disabled={!canWrite || !dirty || saving}
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Kaydet
            </button>
          </div>
        </div>

        {theme !== activeTheme && (
          <p className="px-4 pt-3 text-xs text-slate-400">
            Bu şema şu an yayında değil. Düzen kaydedilir ve şema yayına
            alındığında uygulanır.
          </p>
        )}

        <div className="admin-merit-selected-list">
          {blocks.map((item, index) => (
            <article
              key={item.key}
              className={`admin-merit-selected-row ${item.visible ? '' : 'is-hidden-block'}`}
            >
              <span className="admin-merit-position">{index + 1}</span>
              <span className="admin-merit-game-copy">
                <strong>{HOME_BLOCK_LABELS[item.key] || item.key}</strong>
                <small>
                  {HOME_BLOCK_HINTS[item.key] ? `${HOME_BLOCK_HINTS[item.key]} · ` : ''}
                  <code>[data-bhs-section="{item.key}"]</code>
                  {item.visible ? '' : ' · gizli'}
                </small>
              </span>
              <span className="admin-merit-row-actions">
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  disabled={!canWrite}
                  aria-label={`${HOME_BLOCK_LABELS[item.key] || item.key} ${item.visible ? 'gizle' : 'göster'}`}
                  title={item.visible ? 'Gizle' : 'Göster'}
                >
                  {item.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={!canWrite || index === 0}
                  aria-label={`${HOME_BLOCK_LABELS[item.key] || item.key} yukarı taşı`}
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={!canWrite || index === blocks.length - 1}
                  aria-label={`${HOME_BLOCK_LABELS[item.key] || item.key} aşağı taşı`}
                >
                  <ArrowDown size={14} />
                </button>
              </span>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomeLayout;
